import type { ReactNode } from 'react';
import type { ComparisonDeckStats, PlayerProfile } from '@sleeved/lib';
import { StackedCardAvatars, AutoShrinkText } from '../../components/PlayerWidgetParts';
import {
  palette,
  glass,
  glassBorder,
  glassText,
  fonts,
} from '../../styles/overlayTokens';
import type { AnimPhase } from './chartPrimitives';
import {
  p1rgba,
  p2rgba,
  makeAnimCls,
  SectionDivider,
  DonutTypeChart,
  MiniHeader,
  MkDistributionBar,
  MiniBarChart,
  KeywordBubbleCloud,
  MiniHorizontalBars,
  MiniHeatmap,
} from './chartPrimitives';

// ---------------------------------------------------------------------------
// chronoCore — ChronoCoreDensePanel + ChronoCoreRailPanel. Split out of the
// former DeckOverlayPanels.tsx monolith (pure move, no behavior change).
// ---------------------------------------------------------------------------

/** Chrono Core equipment subtype display order (Visor → Gauntlets → Greaves). */
export const EQUIPMENT_SUBTYPE_ORDER: ReadonlyArray<string> = ['Visor', 'Gauntlets', 'Greaves'];

// ---------------------------------------------------------------------------
// ChronoCoreDensePanel — dense 2-column mini-chart layout for ChronoCore TCG.
// Rendered when compData.gameId === 'chrono-core'. All other games use
// CuratedPlayerPanel. This component contains no gameId branching — it is
// entirely data-driven from props.
// ---------------------------------------------------------------------------

export interface ChronoCoreDensePanelProps {
  side: 'p1' | 'p2';
  stats: ComparisonDeckStats | null;
  peerHasStats: boolean;
  playerName: string;
  archetype: string;
  pilotCardUrl?: string | null;
  coreCardUrl?: string | null;
  /** Chrono Core: Pilot card name — rendered as a name chip in the Starting Loadout group. */
  pilotName?: PlayerProfile['pilotName'];
  /** Chrono Core: Core card name — rendered as a name chip in the Starting Loadout group. */
  coreName?: PlayerProfile['coreName'];
  /** Chrono Core loadout weapon identities — rendered as compact badges in the Starting Loadout group. */
  weaponIdentity?: PlayerProfile['weaponIdentity'];
  /** Chrono Core loadout equipment (Visor/Gauntlets/Greaves) — rendered alongside weapon badges in the Starting Loadout group. */
  equipment?: PlayerProfile['equipment'];
  /** Chrono Core: Pilot card life value — rendered as a prominent flanking stat in the identity zone. Hidden when null/undefined. */
  pilotLife?: PlayerProfile['pilotLife'];
  /** Chrono Core: Core card shield value — rendered as a prominent flanking stat in the identity zone. Hidden when null/undefined. */
  coreShield?: PlayerProfile['coreShield'];
  coreCostSharedMax: number;
  coreCostLabelUnion: string[];
  dmgSharedMax: number;
  chargeSharedMax: number;
  phase: AnimPhase;
  /** Reduced-motion preference threaded from DeckCompareOverlay; avoids inline matchMedia calls per render. */
  reducedMotion: boolean;
  /**
   * When true, the panel expands to fill available flex space (max-width 960px, centered via
   * margin auto). Default (false/absent): fixed 680px flex basis for the compare overlay.
   */
  fullWidth?: boolean;
  /**
   * When true, all DIRECTION logic resolves to left-to-right (P1) orientation regardless of
   * `side`. ACCENT color continues to derive from `side`. Used by the profile widget to render
   * P2's deck in a single-panel left-to-right layout without changing the P2 accent color.
   */
  naturalLayout?: boolean;
  /**
   * Override for the generic no-stats, no-name placeholder string.
   * Falls back to 'Load a deck in the caster console to see stats' when absent.
   */
  placeholderText?: string;
}

export function ChronoCoreDensePanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  pilotCardUrl,
  coreCardUrl,
  pilotName,
  coreName,
  weaponIdentity,
  equipment,
  pilotLife,
  coreShield,
  coreCostSharedMax,
  coreCostLabelUnion,
  dmgSharedMax,
  chargeSharedMax,
  phase,
  reducedMotion,
  fullWidth,
  naturalLayout,
  placeholderText,
}: ChronoCoreDensePanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  // naturalLayout: direction logic resolves to P1 orientation; accent stays keyed on side (isP1).
  const isP1Layout = isP1 || !!naturalLayout;
  const layoutSide: 'p1' | 'p2' = isP1Layout ? 'p1' : side;
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const accentRgba = isP1 ? p1rgba : p2rgba;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const hasPilot = pilotCardUrl != null && pilotCardUrl !== '';
  const hasCore = coreCardUrl != null && coreCardUrl !== '';
  const hasAnyUrl = hasPilot || hasCore;

  // ── Avatar block (119px container from circleSize=84, 58% overlap) ─────────
  // circleSize=84: overlap=round(84*0.58)=49, offset=35, containerSize=35+84=119px
  const AVATAR_CIRCLE_SIZE = 84;
  let avatarBlock: ReactNode;
  if (!hasStats && isOneDeckPlaceholder) {
    // One-deck state: pulsing border rect sized to match the ~119px avatar footprint
    avatarBlock = (
      <div
        className={reducedMotion ? undefined : 'dcao-placeholder-pulse'}
        style={{
          width: 119,
          height: 119,
          borderRadius: 8,
          background: glassBorder.subtle,
          border: `2px solid ${accentColor}`,
          opacity: reducedMotion ? 0.35 : undefined,
          flexShrink: 0,
          margin: '8px 0',
        }}
      />
    );
  } else if (hasAnyUrl && hasStats) {
    // Has pilot/core URLs and a loaded deck: stacked card avatars — no box-shadow per CD polish
    avatarBlock = (
      <div
        className={animCls('dcao-pilot-enter')}
        style={{ flexShrink: 0, margin: '8px 0' }}
      >
        <StackedCardAvatars
          pilotCardUrl={pilotCardUrl}
          coreCardUrl={coreCardUrl}
          circleSize={AVATAR_CIRCLE_SIZE}
          mirror={!isP1Layout}
        />
      </div>
    );
  } else {
    // No URLs, or deck not loaded: static placeholder rect
    avatarBlock = (
      <div style={{
        width: 119,
        height: 119,
        borderRadius: 8,
        background: glassBorder.subtle,
        opacity: 0.5,
        flexShrink: 0,
        margin: '8px 0',
      }} />
    );
  }

  // ── Data lookups — use .data (not .entries) on additionalCurves/subtypeDistributions ──
  const dmgCurve = stats?.additionalCurves?.find(c => /damage/i.test(c.label));
  const chargeCurve = stats?.additionalCurves?.find(c => /charge/i.test(c.label));
  // subtypeDistributions[0] (Trait Breakdown) removed per CD feedback — low value.
  // subtypeDistributions[1] is Equipment Subtype — keep.
  const equipDist = stats?.subtypeDistributions?.[1] ?? null;

  const equipEntries = equipDist?.data ?? [];
  const equipLocalMax = equipEntries.length > 0 ? Math.max(...equipEntries.map(e => e.count)) : 1;

  // Top 16 keywords (full-width row has room for more bubbles).
  const top16Keywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count).slice(0, 16)
    : [];

  // Equipment sorted into canonical subtype order: Visor → Gauntlets → Greaves; other subtypes last.
  // Non-mutating: spreads before sorting so the prop array is never mutated.
  const sortedEquipment = [...(equipment ?? [])].sort((a, b) => {
    const ai = EQUIPMENT_SUBTYPE_ORDER.indexOf(a.subtype ?? '');
    const bi = EQUIPMENT_SUBTYPE_ORDER.indexOf(b.subtype ?? '');
    return (ai === -1 ? EQUIPMENT_SUBTYPE_ORDER.length : ai) - (bi === -1 ? EQUIPMENT_SUBTYPE_ORDER.length : bi);
  });

  // ── Display headers (strip suffixes, uppercase) ─────────────────────────────
  const mkHeader = (stats?.costCurveLabel ?? 'MK DISTRIBUTION').toUpperCase();
  const coreCostHeader = (stats?.secondaryCurveLabel ?? 'CORE COST DISTRIBUTION').toUpperCase();
  const equipHeader = equipDist ? equipDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'EQUIPMENT SUBTYPE';
  const dmgLabel = dmgCurve ? dmgCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'DAMAGE VALUE';
  const chargeLabel = chargeCurve ? chargeCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'CHARGE VALUE';

  // Named helper: builds the 3-row 2-column mini-chart grid cells and returns them
  // in P1/P2 bilateral order. Takes the stats object explicitly so TypeScript sees the
  // non-null type (it is always called inside the hasStats branch). All other values
  // are captured from the enclosing ChronoCoreDensePanel scope.
  const renderDenseGrid = (s: ComparisonDeckStats): ReactNode => {
    /* --- Row 1 cells ---
       Rows 1–3: for P2 the two grid columns are swapped so each panel's
       "secondary" charts (Core Cost, Equipment, Charge) sit left (toward
       the VS center) and "primary" charts (Mk, Card Types, Damage) sit
       right (toward the outer edge), giving true bilateral symmetry.
       Each cell retains its own isP1Layout text-alignment / legend logic — we
       are only changing which column the cell occupies, not its contents.
       isP1Layout governs textAlign, reverse, and layoutSide governs sub-component direction. */
    const mkCell = s.costCurve.length > 0 ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text={mkHeader} />
        <MkDistributionBar
          curve={s.costCurve}
          side={layoutSide}
          accentColor={accentColor}
          phase={phase}
          reverse={!isP1Layout}
        />
      </div>
    ) : <div />;

    const coreCostCell = coreCostLabelUnion.length > 0 && s.secondaryCurve ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text={coreCostHeader} />
        {/* Gap between the header and the tallest bar's count label. A full-height
            (max) bar's count sits at the very top of the chart, so this needs to clear
            the header comfortably. Panel has bottom spare to absorb it. Applies to both
            P1 and P2 since they share this cell definition. */}
        <div style={{ marginTop: 28 }}>
          <MiniBarChart
            curve={s.secondaryCurve}
            labelUnion={coreCostLabelUnion}
            sharedMax={coreCostSharedMax}
            accentColor={accentColor}
            phase={phase}
            reverse={!isP1Layout}
          />
        </div>
      </div>
    ) : <div />;

    /* --- Row 2 cells ---
       legendPosition="side" forces the legend beside the ring for the dense
       cell (~290px wide); P2 mirrors via row-reverse inside DonutTypeChart so
       the ring sits toward the outer panel edge and legend toward the center. */
    // cardTypeDist excludes Pilot/Core (starting-loadout cards, not main-deck types) — matches the rail panel.
    const cardTypeDist = s.typeDistribution.filter(e => !/^(pilot|core)$/i.test(e.label));
    const cardTypesCell = cardTypeDist.length > 0 ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text="CARD TYPES" />
        <DonutTypeChart dist={cardTypeDist} side={layoutSide} phase={phase} size={96} legendPosition="side" />
      </div>
    ) : <div />;

    const equipCell = equipEntries.length > 0 ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text={equipHeader} />
        <MiniHorizontalBars
          entries={equipEntries}
          localMax={equipLocalMax}
          side={layoutSide}
          accentColor={accentColor}
          phase={phase}
          isEntering={isEntering}
          startDelay={740}
        />
      </div>
    ) : <div />;

    /* --- Row 3 cells --- */
    const dmgCell = dmgCurve && dmgCurve.data.length > 0 ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text={dmgLabel} />
        <MiniHeatmap
          data={dmgCurve.data}
          sharedMax={dmgSharedMax}
          accentColor={accentColor}
          phase={phase}
          isEntering={isEntering}
          startDelay={780}
        />
      </div>
    ) : <div />;

    const chargeCell = chargeCurve && chargeCurve.data.length > 0 ? (
      <div style={{ textAlign: isP1Layout ? 'left' : 'right' }}>
        <MiniHeader text={chargeLabel} />
        <MiniHeatmap
          data={chargeCurve.data}
          sharedMax={chargeSharedMax}
          accentColor={accentColor}
          phase={phase}
          isEntering={isEntering}
          startDelay={780}
        />
      </div>
    ) : <div />;

    /* isP1Layout: natural order — col1=Mk/CardTypes/Damage, col2=CoreCost/Equip/Charge
       !isP1Layout: mirrored order — col1=CoreCost/Equip/Charge, col2=Mk/CardTypes/Damage */
    return isP1Layout ? (
      <>
        {mkCell}{coreCostCell}
        {cardTypesCell}{equipCell}
        {dmgCell}{chargeCell}
      </>
    ) : (
      <>
        {coreCostCell}{mkCell}
        {equipCell}{cardTypesCell}
        {chargeCell}{dmgCell}
      </>
    );
  };

  return (
    <div
      className={animCls(isP1Layout ? 'dcao-p1-enter' : 'dcao-p2-enter')}
      style={{
        flex: fullWidth ? 1 : '0 0 680px',
        maxWidth: fullWidth ? 1400 : undefined,
        margin: fullWidth ? '0 auto' : undefined,
        height: '100%',
        position: 'relative',
        background: glass.card,
        borderRadius: 16,
        border: `1px solid ${glassBorder.light}`,
        overflow: 'hidden',
        // isP1Layout governs which edge gets the accent border and which direction the inset shadow runs.
        // accentColor/accentRgba remain keyed on isP1 (side) for color identity.
        // fullWidth: directional side accent border + inner-edge shadow removed — no "toward-center" edge exists.
        ...(isP1Layout
          ? { borderRight: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset -4px 0 20px ${accentRgba(0.12)}` }
          : { borderLeft: fullWidth ? undefined : `2px solid ${accentColor}`, boxShadow: fullWidth ? undefined : `inset 4px 0 20px ${accentRgba(0.12)}` }),
      }}
    >
      {/* Top accent strip */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        borderRadius: '16px 16px 0 0',
        background: accentColor,
      }} />

      {/* Inner content column — top-aligned; id used by content-update effect */}
      <div
        id={`dcao-content-${side}`}
        style={{
          position: 'absolute',
          top: 4,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '32px 36px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          alignItems: isP1Layout ? 'flex-start' : 'flex-end',
        }}
      >
        {/* ── Slim identity zone (min 135px — accommodates 119px avatar footprint + 8px margin each side;
              grows to fit weapon identity badges when present) */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            flexDirection: isP1Layout ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 16,
            minHeight: 135,
            width: '100%',
            flexShrink: 0,
          }}
        >
          {/* Pilot + Core stacked avatars or placeholder rect */}
          {avatarBlock}

          {/* Combined Shield / Life vertical stack — Shield on top, Life below.
              order: 2 places it on the INNER edge (after the name block, toward the VS center),
              the opposite side from the pilot/core avatars. P2 mirrors naturally via the parent
              row-reverse flex direction. Each stat is hidden individually when its value is absent;
              the entire block is omitted when both values are null/undefined. */}
          {(coreShield != null || pilotLife != null) && (
            <div
              aria-hidden="true"
              style={{
                order: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                gap: 10,
                overflow: 'hidden',
              }}
            >
              {coreShield != null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 44, fontFamily: fonts.display, fontWeight: 700, color: accentColor, lineHeight: 1.0 }}>
                    {coreShield}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: glassText.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    SHIELD
                  </div>
                </div>
              )}
              {pilotLife != null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 44, fontFamily: fonts.display, fontWeight: 700, color: accentColor, lineHeight: 1.0 }}>
                    {pilotLife}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: glassText.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    LIFE
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Name block */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 0,
            flex: 1,
            alignItems: isP1Layout ? 'flex-start' : 'flex-end',
            textAlign: isP1Layout ? 'left' : 'right',
          }}>
            {/* Player name (36px) */}
            {hasName ? (
              <div style={{
                fontSize: 36,
                fontFamily: fonts.display,
                fontWeight: 700,
                color: glassText.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.0,
                marginBottom: 4,
                width: '100%',
              }}>
                {playerName}
              </div>
            ) : (
              <div style={{ fontSize: 16, fontWeight: 400, color: glassText.muted, marginBottom: 4 }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}

            {/* Archetype (18px) */}
            {archetype && (
              <div style={{
                fontSize: 18,
                fontWeight: 500,
                color: glassText.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                marginBottom: 3,
                width: '100%',
              }}>
                {archetype}
              </div>
            )}

            {/* Fallback copy when no stats */}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.disabled, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>

        </div>

        {/* ── Starting Loadout section — below identity row, above mini-chart grid.
            Row order (top to bottom): Pilot|Core name chips, Equipment, Weapons.
            Pilot|Core: name-only pill (no role label), Pilot chip first in JSX.
            isP1Layout: row (Pilot leftmost, outer edge). !isP1Layout: row-reverse (Pilot
            rendered rightmost, outer edge) — mirrors the panel's bilateral symmetry.
            Chips render only when their name is present; row omitted entirely if neither exists.
            Weapons: Primary (index 0) and Secondary (index 1) on a single horizontal row,
            side by side, same isP1Layout row/row-reverse mirroring as the Pilot|Core row.
            Equipment: single horizontal line ordered Visor→Gauntlets→Greaves by subtype
            (sorted into sortedEquipment above; other subtypes go last). Pills show name only
            — no subtype tag. isP1Layout: left-aligned. !isP1Layout: right-aligned + row-reverse.
            Section hidden when no pilot/core name, no weapons, AND no equipment. */}
        {((pilotName || coreName) || (weaponIdentity && weaponIdentity.length > 0) || sortedEquipment.length > 0) && (
          <div
            className={animCls('dcao-chip-enter')}
            style={{
              marginTop: 12,
              width: '100%',
              flexShrink: 0,
              textAlign: isP1Layout ? 'left' : 'right',
            }}
          >
            {/* Section label — matches MiniHeader style exactly */}
            <div style={{
              fontSize: 14,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: glassText.disabled,
              marginBottom: 5,
            }}>
              Starting Loadout
            </div>

            {/* Row group — explicit flex gap (not margin-collapse) for inter-row spacing.
                Each of the three rows below (Pilot|Core / Equipment / Weapons) is conditionally
                rendered, so relying on adjacent block margins collapsing was fragile: editing
                one row's margin independently would silently change the rendered gap. `gap`
                only applies between rows actually present in the DOM, so no per-row
                marginTop/marginBottom is needed to produce (or suppress) leading/trailing
                spacing. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

            {/* Pilot | Core row — Pilot chip first (JSX order), then Core.
                isP1Layout: row order (Pilot pill first, left/outer edge).
                !isP1Layout: row-reverse (Pilot pill first rendered rightmost, outer edge). */}
            {(pilotName || coreName) && (
              <div style={{
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                gap: 6,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}>
                {pilotName && (
                  <div style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: isP1Layout ? 'flex-start' : 'flex-end',
                    gap: 1,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: glassBorder.subtle,
                    border: `1px solid ${glassBorder.light}`,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pilotName}
                    </span>
                  </div>
                )}
                {coreName && (
                  <div style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: isP1Layout ? 'flex-start' : 'flex-end',
                    gap: 1,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: glassBorder.subtle,
                    border: `1px solid ${glassBorder.light}`,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {coreName}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Equipment row — Visor | Gauntlets | Greaves on one horizontal line, no wrap.
                isP1Layout: row (Visor leftmost, under left-edge avatar).
                !isP1Layout: row-reverse (Visor rightmost, under right-edge avatar).
                Pills ellipsis rather than wrap when content is long. */}
            {sortedEquipment.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                gap: 6,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}>
                {sortedEquipment.map((equip, idx) => (
                  <div
                    key={equip.subtype ?? idx}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: glassBorder.subtle,
                      border: `1px solid ${glassBorder.light}`,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {equip.name}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Weapons row — Primary and Secondary side by side on one horizontal row.
                isP1Layout: row order (Primary pill first, left/outer edge).
                !isP1Layout: row-reverse (Primary pill first rendered rightmost, outer edge).
                If only one weapon exists, that pill renders alone. */}
            {weaponIdentity && (weaponIdentity[0] || weaponIdentity[1]) && (
              <div style={{
                display: 'flex',
                flexDirection: isP1Layout ? 'row' : 'row-reverse',
                gap: 6,
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}>
                {weaponIdentity[0] && (
                  <div style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: isP1Layout ? 'flex-start' : 'flex-end',
                    gap: 1,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: glassBorder.subtle,
                    border: `1px solid ${glassBorder.light}`,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {weaponIdentity[0].name}
                    </span>
                    {weaponIdentity[0].weaponType && (
                      <span style={{ fontSize: 12, fontWeight: 400, color: glassText.muted, lineHeight: 1.3, flexShrink: 0 }}>
                        {weaponIdentity[0].weaponType}
                      </span>
                    )}
                  </div>
                )}
                {weaponIdentity[1] && (
                  <div style={{
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: isP1Layout ? 'flex-start' : 'flex-end',
                    gap: 1,
                    padding: '3px 8px',
                    borderRadius: 6,
                    background: glassBorder.subtle,
                    border: `1px solid ${glassBorder.light}`,
                    minWidth: 0,
                    overflow: 'hidden',
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: glassText.primary, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {weaponIdentity[1].name}
                    </span>
                    {weaponIdentity[1].weaponType && (
                      <span style={{ fontSize: 12, fontWeight: 400, color: glassText.muted, lineHeight: 1.3, flexShrink: 0 }}>
                        {weaponIdentity[1].weaponType}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {/* ── Stats or no-deck placeholder ───────────────────────────────── */}
        {hasStats ? (
          <>
            {/* 2-column mini-chart grid
                Row 1: Mk Distribution | Core Cost Distribution
                Row 2: Card Types      | Equipment Subtype
                Row 3: Damage Value    | Charge Value
                Row 4: Keywords (full-width, grid-column: 1 / -1)
                textAlign on each cell cascades to MiniHeader for P2 right-alignment. */}
            <div style={{
              marginTop: 10,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: 28,
              rowGap: 10,
              width: '100%',
              alignItems: 'start',
              overflow: 'hidden',
            }}>
              {renderDenseGrid(stats)}

              {/* Row 4 — Keywords full-width (grid-column: 1 / -1, up to 16 bubbles) */}
              {top16Keywords.length > 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: isP1Layout ? 'left' : 'right' }}>
                  <MiniHeader text="KEYWORDS" />
                  <KeywordBubbleCloud
                    keywords={top16Keywords}
                    side={layoutSide}
                    accentColor={accentColor}
                    phase={phase}
                    isEntering={isEntering}
                    startDelay={840}
                  />
                </div>
              ) : null}
            </div>
          </>
        ) : (
          /* No-stats placeholder — same deck icon as curated layout */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 12,
          }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.4 }}>
              <rect x="4" y="8" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
              <rect x="8" y="4" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
            </svg>
            <div style={{
              fontSize: 16,
              fontWeight: 400,
              color: glassText.muted,
              textAlign: 'center',
              maxWidth: 240,
            }}>
              {hasName ? 'Awaiting deck…' : (placeholderText ?? 'Load a deck in the caster console to see stats')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChronoCoreRailPanel — purpose-built condensed stats rail for the Deck Profile
// overlay's left column (~35% width). Replaces ScaleToFit + ChronoCoreDensePanel
// which collapsed to 2px because ChronoCoreDensePanel needs full width/height to
// lay out correctly.
//
// Design contract:
// - naturalLayout is IMPLICIT TRUE — all direction logic is left-to-right.
// - accentColor still derives from `side` for brand identity.
// - Sections (top to bottom):
//     1. Identity + vitals (full width) — pilot avatar, name, archetype, SHIELD/LIFE.
//     2. Paired row A: MK Distribution (left) | Core Cost Distribution (right).
//     3. Paired row B: Card Types (left) | Equipment Subtype (right) — half-column donut
//        (size=72, legendPosition="below") paired with the Equipment Subtype bars.
//     4. Paired row C: Damage Value heatmap (left) | Charge Value heatmap (right).
//     5. Keywords full-width bottom row (all by count, compact bubbles).
// - Weapons section REMOVED — the card grid on the right already shows those cards.
// - Each paired row: flex row, gap 16, two flex:1 minWidth:0 columns.
// - DonutTypeChart: legendPosition="below" + size=72 for the half-column Card Types cell.
// - MiniBarChart compact=true for Core Cost; reduces chart height to 50px.
// - SectionDivider with margin="12px 0" (tighter than the 20px default).
// ---------------------------------------------------------------------------

export type ChronoCoreRailPanelProps = Omit<
  ChronoCoreDensePanelProps,
  'fullWidth' | 'naturalLayout'
>;

export function ChronoCoreRailPanel({
  side,
  stats,
  peerHasStats,
  playerName,
  archetype,
  pilotCardUrl,
  coreCardUrl,
  // weaponIdentity, equipment: accepted (part of shared props type) but not rendered —
  // the Weapons section is removed; cards in the right grid already show equipment/weapons.
  pilotLife,
  coreShield,
  coreCostSharedMax,
  coreCostLabelUnion,
  dmgSharedMax,
  chargeSharedMax,
  phase,
  reducedMotion,
  placeholderText,
}: ChronoCoreRailPanelProps) {
  const animCls = makeAnimCls(phase);
  const isEntering = phase === 'entering';
  const isP1 = side === 'p1';
  // naturalLayout is implicit — direction is always left-to-right.
  // accentColor continues to derive from side for brand identity.
  const accentColor = isP1 ? palette.coralGlow : palette.brilliantRose;
  const hasStats = stats !== null;
  const hasName = playerName.trim().length > 0;
  // NOTE: peerHasStats is always false in the single-slot profile context; the pulsing-placeholder branch only fires if this panel is reused in a two-slot (compare) view.
  const isOneDeckPlaceholder = !hasStats && peerHasStats;

  const hasPilot = pilotCardUrl != null && pilotCardUrl !== '';
  const hasCore = coreCardUrl != null && coreCardUrl !== '';
  const hasAnyUrl = hasPilot || hasCore;

  // circleSize=64: overlap=round(64*0.58)=37, offset=27, containerSize=91px
  const AVATAR_CIRCLE_SIZE = 64;
  const AVATAR_CONTAINER_SIZE = 91;

  // Avatar block — mirrors ChronoCoreDensePanel's three-state logic with circleSize=64.
  let avatarBlock: ReactNode;
  if (!hasStats && isOneDeckPlaceholder) {
    // One-deck state: pulsing border rect at the avatar footprint size.
    avatarBlock = (
      <div
        className={reducedMotion ? undefined : 'dcao-placeholder-pulse'}
        style={{
          width: AVATAR_CONTAINER_SIZE,
          height: AVATAR_CONTAINER_SIZE,
          borderRadius: 8,
          background: glassBorder.subtle,
          border: `2px solid ${accentColor}`,
          opacity: reducedMotion ? 0.35 : undefined,
          flexShrink: 0,
        }}
      />
    );
  } else if (hasAnyUrl && hasStats) {
    avatarBlock = (
      <div className={animCls('dcao-pilot-enter')} style={{ flexShrink: 0 }}>
        <StackedCardAvatars
          pilotCardUrl={pilotCardUrl}
          coreCardUrl={coreCardUrl}
          circleSize={AVATAR_CIRCLE_SIZE}
          mirror={false}
          avatarShape="circle"
        />
      </div>
    );
  } else {
    // Zero-deck / no-URL state: static muted rect.
    avatarBlock = (
      <div style={{
        width: AVATAR_CONTAINER_SIZE,
        height: AVATAR_CONTAINER_SIZE,
        borderRadius: 8,
        background: glassBorder.subtle,
        opacity: 0.5,
        flexShrink: 0,
      }} />
    );
  }

  // ── Data lookups (mirrors ChronoCoreDensePanel) ──────────────────────────
  // additionalCurves: find by label regex — same field path used in ChronoCoreDensePanel.
  const dmgCurve = stats?.additionalCurves?.find(c => /damage/i.test(c.label));
  const chargeCurve = stats?.additionalCurves?.find(c => /charge/i.test(c.label));
  // subtypeDistributions[0] = Trait Breakdown (removed per CD feedback).
  // subtypeDistributions[1] = Equipment Subtype — used for Paired Row B right column.
  const equipDist = stats?.subtypeDistributions?.[1] ?? null;
  const equipEntries = equipDist?.data ?? [];
  const equipLocalMax = equipEntries.length > 0 ? Math.max(...equipEntries.map(e => e.count)) : 1;
  // Card Types donut excludes Pilot/Core — these are starting-loadout cards (count 1 each),
  // not main-deck card types. Case-insensitive, exact-match so labels like "Configuration"
  // are never accidentally dropped.
  const cardTypeDist = (stats?.typeDistribution ?? []).filter(e => !/^(pilot|core)$/i.test(e.label));

  // ── Section header text (strip suffixes, uppercase — mirrors ChronoCoreDensePanel) ──
  const mkHeader = (stats?.costCurveLabel ?? 'MK DISTRIBUTION').toUpperCase();
  const coreCostHeader = (stats?.secondaryCurveLabel ?? 'CORE COST DISTRIBUTION').toUpperCase();
  const equipHeader = equipDist ? equipDist.label.replace(/ Breakdown$/i, '').toUpperCase() : 'EQUIPMENT SUBTYPE';
  const dmgLabel = dmgCurve ? dmgCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'DAMAGE VALUE';
  const chargeLabel = chargeCurve ? chargeCurve.label.replace(/ Distribution$/i, '').toUpperCase() : 'CHARGE VALUE';

  // All keywords sorted by count — show as many as fit in the available vertical space.
  // compact=true bubbles (3px/8px padding, 12-15px font) pack more rows into the rail height.
  // The outer overflow:hidden on the content wrapper clips gracefully if any remain offscreen.
  const allKeywords = stats?.keywordDistribution
    ? [...stats.keywordDistribution].sort((a, b) => b.count - a.count)
    : [];

  return (
    <div style={{
      flex: 1,
      height: '100%',
      position: 'relative',
      background: glass.card,
      border: `1px solid ${glassBorder.light}`,
      borderRadius: 16,
      overflow: 'hidden',
    }}>
      {/* Top accent strip — P1: coralGlow, P2: brilliantRose */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: accentColor,
      }} />

      {/* Content wrapper — absolute fill below the 4px accent strip */}
      <div style={{
        position: 'absolute',
        top: 4,
        left: 0,
        right: 0,
        bottom: 0,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        overflow: 'hidden',
      }}>

        {/* ── 1. Identity block (full width) ───────────────────────────────── */}
        <div
          className={animCls('dcao-names-enter')}
          style={{
            display: 'flex',
            gap: 16,
            alignItems: 'center',
            minHeight: 110,
            flexShrink: 0,
          }}
        >
          {/* Stacked card avatars or placeholder rect */}
          <div style={{ flexShrink: 0 }}>
            {avatarBlock}
          </div>

          {/* Name + archetype — flex:1 takes remaining space, ellipsis on overflow */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {hasName ? (
              // Fit-to-width font scaling (28→18px floor) instead of ellipsis truncation —
              // long names (e.g. "Romulus Arnodt") stay fully legible rather than clipping.
              <div style={{ marginBottom: 4 }}>
                <AutoShrinkText
                  text={playerName}
                  maxFontSize={28}
                  minFontSize={18}
                  fontWeight={700}
                  color={glassText.primary}
                  fontFamily={fonts.display}
                />
              </div>
            ) : (
              <div style={{
                fontSize: 14,
                fontWeight: 400,
                color: glassText.muted,
                fontStyle: 'italic',
                marginBottom: 4,
              }}>
                {isP1 ? 'Player 1' : 'Player 2'}
              </div>
            )}
            {archetype && (
              <div style={{
                fontSize: 15,
                fontWeight: 500,
                color: glassText.secondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {archetype}
              </div>
            )}
            {!hasStats && !archetype && (
              <div style={{ fontSize: 14, fontWeight: 400, color: glassText.muted, fontStyle: 'italic' }}>
                {hasName ? 'Awaiting deck...' : 'No deck loaded'}
              </div>
            )}
          </div>

          {/* Shield / Life flanking stats — only when at least one value is present.
              aria-hidden: decorative numeric readout for screen-reader contexts. */}
          {(coreShield != null || pilotLife != null) && (
            <div
              aria-hidden="true"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
              }}
            >
              {coreShield != null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 32, fontFamily: fonts.display, fontWeight: 700, color: accentColor, lineHeight: 1.0 }}>
                    {coreShield}
                  </div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: glassText.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>
                    SHIELD
                  </div>
                </div>
              )}
              {pilotLife != null && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 32, fontFamily: fonts.display, fontWeight: 700, color: accentColor, lineHeight: 1.0 }}>
                    {pilotLife}
                  </div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: glassText.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}>
                    LIFE
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <SectionDivider margin="12px 0" />

        {/* ── Stats sections or no-stats placeholder ──────────────────────── */}
        {hasStats ? (
          <>
            {/* ── Paired Row A: MK Distribution (left) | Core Cost (right) ─── */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, flexShrink: 0 }}>
              {/* MK Distribution — proportional bar + flex-wrap legend */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <MiniHeader text={mkHeader} />
                {stats.costCurve.length > 0 ? (
                  <MkDistributionBar
                    curve={stats.costCurve}
                    side="p1"
                    accentColor={accentColor}
                    phase={phase}
                    reverse={false}
                    legendLayout="list"
                  />
                ) : <div />}
              </div>
              {/* Core Cost Distribution — compact mini bar chart (50px chart height) */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {coreCostLabelUnion.length > 0 && stats.secondaryCurve ? (
                  <>
                    <MiniHeader text={coreCostHeader} />
                    {/* marginTop:28 matches ChronoCoreDensePanel's header→chart gap —
                        same visible spacing as the compare overlay's Core Cost cell.
                        compact=true retained to keep bar height at 50px in the narrower
                        25% column; the gap (not bar height) is what the user aligned. */}
                    <div style={{ marginTop: 28 }}>
                      <MiniBarChart
                        curve={stats.secondaryCurve}
                        labelUnion={coreCostLabelUnion}
                        sharedMax={coreCostSharedMax}
                        accentColor={accentColor}
                        phase={phase}
                        reverse={false}
                        compact={true}
                      />
                    </div>
                  </>
                ) : <div />}
              </div>
            </div>

            <SectionDivider margin="12px 0" />

            {/* ── Paired Row B: Card Types (left) | Equipment Subtype (right) ───
                Card Types uses a half-column donut: size=72 with legendPosition="below"
                (matches the half-column precedent at ChronoCoreDensePanel's Row 2
                cardTypesCell, ~L1709/L1712, which also uses size≈96 at ~290px — here the
                rail's half-column is narrower still at ~198px, so the legend stacks below
                the ring rather than beside it to stay legible). cardTypeDist excludes
                Pilot/Core (starting-loadout cards, not main-deck card types). */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {cardTypeDist.length > 0 ? (
                  <>
                    <MiniHeader text="CARD TYPES" />
                    <DonutTypeChart
                      dist={cardTypeDist}
                      side="p1"
                      phase={phase}
                      size={72}
                      legendPosition="below"
                    />
                  </>
                ) : <div />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {equipEntries.length > 0 ? (
                  <>
                    <MiniHeader text={equipHeader} />
                    <MiniHorizontalBars
                      entries={equipEntries}
                      localMax={equipLocalMax}
                      side="p1"
                      accentColor={accentColor}
                      phase={phase}
                      isEntering={isEntering}
                      startDelay={740}
                    />
                  </>
                ) : <div />}
              </div>
            </div>

            {/* ── Paired Row C: Damage Value heatmap (left) | Charge Value (right) ── */}
            {/* Row rendered only when at least one heatmap curve has data. */}
            {(dmgCurve != null && dmgCurve.data.length > 0 || chargeCurve != null && chargeCurve.data.length > 0) && (
              <>
                <SectionDivider margin="12px 0" />
                <div style={{ display: 'flex', flexDirection: 'row', gap: 16, flexShrink: 0 }}>
                  {/* Damage Value heatmap — uses stats.additionalCurves[damage] */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {dmgCurve != null && dmgCurve.data.length > 0 ? (
                      <>
                        <MiniHeader text={dmgLabel} />
                        <MiniHeatmap
                          data={dmgCurve.data}
                          sharedMax={dmgSharedMax}
                          accentColor={accentColor}
                          phase={phase}
                          isEntering={isEntering}
                          startDelay={780}
                        />
                      </>
                    ) : <div />}
                  </div>
                  {/* Charge Value heatmap — uses stats.additionalCurves[charge] */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {chargeCurve != null && chargeCurve.data.length > 0 ? (
                      <>
                        <MiniHeader text={chargeLabel} />
                        <MiniHeatmap
                          data={chargeCurve.data}
                          sharedMax={chargeSharedMax}
                          accentColor={accentColor}
                          phase={phase}
                          isEntering={isEntering}
                          startDelay={780}
                        />
                      </>
                    ) : <div />}
                  </div>
                </div>
              </>
            )}

            {/* ── Keywords (full width, bottom) — all keywords, compact bubbles ── */}
            {/* compact=true: 12-15px font, 3px/8px padding — packs more rows into the
                remaining vertical space. The outer overflow:hidden clips the last row
                gracefully if content marginally exceeds available height. */}
            {allKeywords.length > 0 && (
              <>
                <SectionDivider margin="12px 0" />
                <div style={{ flexShrink: 0 }}>
                  <MiniHeader text="KEYWORDS" />
                  <KeywordBubbleCloud
                    keywords={allKeywords}
                    side="p1"
                    accentColor={accentColor}
                    phase={phase}
                    isEntering={isEntering}
                    startDelay={550}
                    compact={true}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          /* No-stats placeholder — same deck icon + text as ChronoCoreDensePanel */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 12,
          }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ opacity: 0.4 }}>
              <rect x="4" y="8" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
              <rect x="8" y="4" width="28" height="36" rx="3" stroke={glassText.disabled} strokeWidth="2" />
            </svg>
            <div style={{
              fontSize: 16,
              fontWeight: 400,
              color: glassText.muted,
              textAlign: 'center',
              maxWidth: 240,
            }}>
              {hasName ? 'Awaiting deck…' : (placeholderText ?? 'Load a deck in the caster console to see stats')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

